import type React from "react";
import { useContext } from "react";

import { Stack } from "@mui/material";
import { isFuture, parseISO, startOfDay } from "date-fns";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { getPatientCareEventsQueryKey, removePatientCareEvent } from "api/hooks/useGetPatientCareEventsQuery";
import { getPTCareEventsQueryKey } from "api/hooks/useGetPTCareEventsQuery";
import { CancelIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { removeNote } from "routes/patients/queries/notesApiHandler";
import { BaseButton } from "shared/atoms/BaseButton";
import { Hidden } from "shared/atoms/Hidden";
import { Notification } from "shared/atoms/Notification";
import { type MedicalNoteType, MedicalNoteTypes } from "types";
import { CurrentPatientContext } from "utils/contexts";
import { isKickOffNote } from "utils/medicalNotes";
import { isRTMInitial } from "utils/medicalNotes/isNoteType";
import { isHybridPatient } from "utils/patient";

import { ICDWarningPopup } from "../../PatientHeader/SignNotesTable/components/ICDWarningPopup";
import { isICDCodeMatchingTreatment } from "../../PatientHeader/SignNotesTable/utils/ICDCodes";
import type { ShowButtons } from "../../PatientHeader/SignNotesTable/utils/types";

interface Props {
  cid: number;
  confirmDisabled?: boolean;
  medicalNoteType: MedicalNoteType;
  showConfirmation: ShowButtons;
  setReadOnly: (newReadOnly: boolean) => void;
  setShowConfirmation: (newShow: ShowButtons) => void;
  discardFromDashboard?: () => void;
  startTime?: string;
}

const MedicalNoteFormButtons: React.FC<Props> = ({
  cid,
  confirmDisabled,
  medicalNoteType,
  showConfirmation,
  setReadOnly,
  setShowConfirmation,
  discardFromDashboard,
  startTime,
}) => {
  const {
    getValues,
    trigger,
    formState: { isValid },
  } = useFormContext();

  const { patient } = useContext(CurrentPatientContext);
  const patientId = Number(useParams().patientId);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { profile } = useProfileContext();
  invariant(profile);

  const NOT_DANGER_OR_SUCCESS = !(
    showConfirmation?.type === "saved" ||
    showConfirmation?.type === "success" ||
    showConfirmation?.type === "danger"
  );

  const isFutureDate = startTime ? isFuture(startOfDay(parseISO(startTime))) : false;

  // Signing of notes different from kickoff, supplemenetal and discharge is disabled if the treatment is not started
  const isSigningDisabled = !(
    isKickOffNote(medicalNoteType) ||
    isRTMInitial(medicalNoteType) ||
    medicalNoteType.toLocaleUpperCase() === MedicalNoteTypes.SUPPLEMENTAL ||
    medicalNoteType.toLocaleUpperCase() === MedicalNoteTypes.DISCHARGE ||
    medicalNoteType.toLocaleUpperCase() === MedicalNoteTypes.INITIAL_RE_EVAL ||
    medicalNoteType.toLocaleUpperCase() === MedicalNoteTypes.INITIAL_EVAL ||
    medicalNoteType.toLocaleUpperCase() === MedicalNoteTypes.CALL_HYBRID ||
    medicalNoteType.toLocaleUpperCase() === MedicalNoteTypes.CHAT_HYBRID ||
    patient.treatment_started ||
    isHybridPatient(patient)
  );

  const initiateSigning = async () => {
    await trigger();

    if (isValid) {
      setReadOnly(true);
      setShowConfirmation({ isOpen: true, type: "info" });
    }
  };

  const initiateDelete = async () => {
    if (patientId) {
      const key = getPatientCareEventsQueryKey({ patientId, signed: false });
      queryClient.cancelQueries(key);
      const rollbackData = queryClient.getQueryData(key);
      queryClient.setQueryData(key, removePatientCareEvent(cid));
      try {
        await removeNote(cid);
      } catch {
        queryClient.setQueryData(key, rollbackData);
        setShowConfirmation({ isOpen: true, type: "danger" });
      } finally {
        queryClient.invalidateQueries(key);
        queryClient.invalidateQueries(getPTCareEventsQueryKey({ userId: profile.id }));
      }
    } else {
      try {
        await removeNote(cid);
        if (discardFromDashboard) discardFromDashboard();
      } catch {
        setShowConfirmation({ isOpen: true, type: "danger" });
      }
    }
  };

  const getTreatmentICDCode = () => {
    switch (patient.ailment) {
      case "joint_pain":
        // must match up ICD code type "back" with pain location "lower_back"
        return patient.most_painful_location?.location === "lower_back"
          ? "back"
          : patient.most_painful_location?.location;
      case "osteoporosis":
        return "osteoporosis";
      default:
        return undefined;
    }
  };

  const renderNotificationButtons = (type: ShowButtons["type"]) => {
    switch (type) {
      case "danger":
        return (
          <BaseButton variant="primary" type="button" onClick={() => setShowConfirmation({ isOpen: false })}>
            {t("buttons.ok")}
          </BaseButton>
        );
      case "warning":
        return (
          <BaseButton variant="primary" type="button" data-testid="confirm-delete-button" onClick={initiateDelete}>
            {t("buttons.delete")}
          </BaseButton>
        );
      case "saved":
      case "success":
        return null;
      case "info":
      default:
        return (
          <>
            {isKickOffNote(medicalNoteType) &&
              isICDCodeMatchingTreatment(getValues("icd_codes"), getTreatmentICDCode()) === false && (
                <ICDWarningPopup content={t("icd_popup.message")} />
              )}
            <BaseButton variant="primary" disabled={confirmDisabled} data-testid="confirm-sign-button" type="submit">
              {t("buttons.confirm_sign")}
            </BaseButton>
          </>
        );
    }
  };

  if (showConfirmation?.isOpen) {
    return (
      <>
        <Hidden type="belowTablet">
          <Notification variant={showConfirmation?.type}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1}>
              {/* FIXME: type translation */}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <span>{t(`patients.notes.confirm.${showConfirmation?.type}`)}</span>
              <Stack direction="row">
                {NOT_DANGER_OR_SUCCESS && (
                  <BaseButton
                    variant="text"
                    data-testid="cancel-note-button"
                    onClick={() => {
                      setReadOnly(false);
                      setShowConfirmation({ isOpen: false });
                    }}
                  >
                    {t("buttons.cancel")}
                  </BaseButton>
                )}
                {renderNotificationButtons(showConfirmation?.type)}
              </Stack>
            </Stack>
          </Notification>
        </Hidden>
        <Hidden type="aboveTablet">
          <Notification variant={showConfirmation?.type}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {/* FIXME: type translation */}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <span>{t(`patients.notes.confirm.${showConfirmation?.type}`)}</span>
            </Stack>
          </Notification>
          <Stack direction="column-reverse" alignItems="center" justifyContent="center" mt={1}>
            {NOT_DANGER_OR_SUCCESS && (
              <BaseButton
                variant="text"
                data-testid="cancel-note-button"
                onClick={() => setShowConfirmation({ isOpen: false })}
              >
                {t("buttons.cancel")}
              </BaseButton>
            )}
            {renderNotificationButtons(showConfirmation?.type)}
          </Stack>
        </Hidden>
      </>
    );
  }

  return (
    <Stack
      direction={{ xs: "column-reverse", sm: "row" }}
      justifyContent="flex-end"
      gap={2}
      sx={{
        "& button:first-of-type": { ml: { xs: 0, md: 2 } },
      }}
    >
      {isSigningDisabled && (
        <DisabledSigningWarning data-testid="disabled-signing-warning">
          {t("patients.notes.disabled_signing")}
        </DisabledSigningWarning>
      )}
      {isFutureDate && !isSigningDisabled && (
        <DisabledSigningWarning data-testid="future-date-signing-warning">
          {t("patients.notes.future_date_signing")}
        </DisabledSigningWarning>
      )}
      <BaseButton
        variant="tertiary"
        data-testid="medical-note-delete-button"
        onClick={() => {
          setReadOnly(true);
          setShowConfirmation({ isOpen: true, type: "warning" });
        }}
      >
        <CancelIcon />
        {t("buttons.delete")}
      </BaseButton>
      <BaseButton
        variant="primary"
        data-testid="sign-button"
        onClick={initiateSigning}
        disabled={isSigningDisabled || isFutureDate}
      >
        {t("buttons.sign")}
      </BaseButton>
    </Stack>
  );
};

const DisabledSigningWarning = styled.div`
  color: ${props => props.theme.colors.redesign.r100};
  display: flex;
  align-items: center;
`;

export { MedicalNoteFormButtons };
